/**
 * Context provider for online/offline status management.
 * Handles detection, sync state, and provides utilities for offline-aware components.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { onlineManager } from '@tanstack/react-query';
import { offlineStorage, OfflineStorageService } from './OfflineStorageService';

interface OfflineStatusContextType {
  /** Whether the browser reports being online */
  isOnline: boolean;
  /** Whether we have actual connectivity to the backend */
  hasConnectivity: boolean;
  /** Last successful sync timestamp */
  lastSyncedAt: Date | null;
  /** Current sync status */
  syncStatus: 'idle' | 'syncing' | 'error';
  /** Error message if sync failed */
  syncError: string | null;
  /** Force a sync attempt */
  forceSync: () => Promise<void>;
  /** Whether offline storage is available */
  isOfflineCapable: boolean;
}

const OfflineStatusContext = createContext<OfflineStatusContextType | undefined>(undefined);

// Health check endpoint
const HEALTH_CHECK_URL = '/health';
const CONNECTIVITY_CHECK_INTERVAL = 30000; // 30 seconds
const INITIAL_CHECK_DELAY = 1000; // Wait 1s after mount before first check
const CONSECUTIVE_FAILURES_THRESHOLD = 2; // Require 2 consecutive failures before showing offline

interface OfflineStatusProviderProps {
  children: React.ReactNode;
}

export function OfflineStatusProvider({ children }: OfflineStatusProviderProps) {
  // Single source of truth for connectivity: React Query's `onlineManager`.
  // On native (Capacitor) it is driven by the `@capacitor/network` plugin
  // (wired up in index.tsx), which reports Airplane Mode reliably where
  // `navigator.onLine` lies inside WKWebView. On web it falls back to the
  // browser online/offline events. Reading it here keeps every offline-aware
  // UI surface in sync with the queries themselves.
  const [isOnline, setIsOnline] = useState(() => onlineManager.isOnline());
  // Seed from onlineManager: if the device is already offline at cold start
  // (e.g. mobile Airplane Mode) we want to reflect that immediately rather than
  // flash "online". When online we stay optimistic and verify actual backend
  // reachability with a delayed health check after mount.
  const [hasConnectivity, setHasConnectivity] = useState(() => onlineManager.isOnline());
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'error'>('idle');
  const [syncError, setSyncError] = useState<string | null>(null);
  const [isOfflineCapable] = useState(() => OfflineStorageService.isAvailable());

  const connectivityCheckRef = useRef<NodeJS.Timeout | null>(null);
  const consecutiveFailuresRef = useRef(0);
  const initialCheckDoneRef = useRef(false);

  // Load last sync time from storage on mount
  useEffect(() => {
    const loadLastSyncTime = async () => {
      const lastSync = await offlineStorage.getLastSyncTime();
      if (lastSync) {
        setLastSyncedAt(lastSync);
      }
    };
    loadLastSyncTime();
  }, []);

  // Check actual connectivity by pinging the health endpoint
  const checkConnectivity = useCallback(async (): Promise<boolean> => {
    // Skip connectivity check on localhost during development
    if (window.location.hostname === 'localhost') {
      return navigator.onLine;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(HEALTH_CHECK_URL, {
        method: 'GET',
        cache: 'no-store',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        // Reset failure counter on success
        consecutiveFailuresRef.current = 0;
        return true;
      }

      // Non-ok response counts as failure
      consecutiveFailuresRef.current++;
      return consecutiveFailuresRef.current < CONSECUTIVE_FAILURES_THRESHOLD;
    } catch (error) {
      // Don't count aborted requests (from navigation) as failures
      if (error instanceof DOMException && error.name === 'AbortError') {
        // Return current connectivity state on abort (don't flip to offline)
        return true;
      }

      consecutiveFailuresRef.current++;
      // Only report offline after consecutive failures
      return consecutiveFailuresRef.current < CONSECUTIVE_FAILURES_THRESHOLD;
    }
  }, []);

  // Track connectivity via React Query's onlineManager (Capacitor-backed on
  // native, browser-event-backed on web) rather than `navigator.onLine`
  // directly, so this context and the query layer never disagree.
  useEffect(() => {
    const unsubscribe = onlineManager.subscribe((online: boolean) => {
      setIsOnline(online);
      if (online) {
        // Came (back) online — verify the backend is actually reachable.
        void checkConnectivity().then(setHasConnectivity);
      } else {
        setHasConnectivity(false);
      }
    });

    return unsubscribe;
  }, [checkConnectivity]);

  // Periodic connectivity check when online
  useEffect(() => {
    if (!isOnline) {
      return;
    }

    const runCheck = async () => {
      const connected = await checkConnectivity();
      setHasConnectivity(connected);
    };

    // Delay the initial check to avoid race conditions during page load/navigation
    // This prevents false "offline" banners when requests are canceled during hydration
    const initialDelay = setTimeout(() => {
      initialCheckDoneRef.current = true;
      runCheck();

      // Start periodic checks after initial check
      connectivityCheckRef.current = setInterval(runCheck, CONNECTIVITY_CHECK_INTERVAL);
    }, INITIAL_CHECK_DELAY);

    return () => {
      clearTimeout(initialDelay);
      if (connectivityCheckRef.current) {
        clearInterval(connectivityCheckRef.current);
      }
    };
  }, [isOnline, checkConnectivity]);

  // Force sync function
  const forceSync = useCallback(async () => {
    if (!hasConnectivity) {
      setSyncError('No connectivity');
      setSyncStatus('error');
      return;
    }

    setSyncStatus('syncing');
    setSyncError(null);

    try {
      // For now, just verify connectivity and update sync time
      // In a full implementation, this would call the sync endpoint
      const connected = await checkConnectivity();

      if (connected) {
        const now = new Date();
        await offlineStorage.setLastSyncTime(now);
        setLastSyncedAt(now);
        setSyncStatus('idle');
      } else {
        throw new Error('Failed to connect to server');
      }
    } catch (error) {
      setSyncError(error instanceof Error ? error.message : 'Sync failed');
      setSyncStatus('error');
    }
  }, [hasConnectivity, checkConnectivity]);

  const value: OfflineStatusContextType = {
    isOnline,
    hasConnectivity,
    lastSyncedAt,
    syncStatus,
    syncError,
    forceSync,
    isOfflineCapable,
  };

  return (
    <OfflineStatusContext.Provider value={value}>
      {children}
    </OfflineStatusContext.Provider>
  );
}

/**
 * Hook to access offline status.
 */
export function useOfflineStatus(): OfflineStatusContextType {
  const context = useContext(OfflineStatusContext);
  if (context === undefined) {
    throw new Error('useOfflineStatus must be used within an OfflineStatusProvider');
  }
  return context;
}

/**
 * Hook that returns true if the app is online with connectivity.
 */
export function useIsOnline(): boolean {
  const { hasConnectivity } = useOfflineStatus();
  return hasConnectivity;
}

/**
 * Hook that returns true if the app is offline.
 */
export function useIsOffline(): boolean {
  const { hasConnectivity } = useOfflineStatus();
  return !hasConnectivity;
}

/**
 * Non-throwing variant of {@link useIsOffline} for leaf UI that may legitimately
 * render in a tree without the provider — e.g. EntityLoadState, which is mounted
 * by ~every detail/list page and in isolated unit tests. When no provider is
 * present it assumes online (returns false), so the component falls back to its
 * load-failed state rather than crashing the whole page. The real app always
 * mounts OfflineStatusProvider, so this only differs under test/storybook.
 */
export function useIsOfflineOptional(): boolean {
  const context = useContext(OfflineStatusContext);
  return context ? !context.hasConnectivity : false;
}
